import _flatten from 'lodash-es/flatten';
import * as ko from 'knockout';
import {
    ChecklistDto,
    DestinationDto,
    destinationsApi,
    FilterGroupItemsDto,
    myTripChecklistApi,
    myTripsApi,
    publicApi,
    TripDto
} from '../../api/api-wrapper';
import {Context, Router} from '@profiscience/knockout-contrib-router';
import {getSubfilters} from "../../api/api-helpers";
import 'knockout.autocomplete/lib/knockout.autocomplete';
import 'knockout.autocomplete/lib/knockout.autocomplete.css';
import './filter';
import {postbox} from "../../components/util/postbox";
import globalState from '../../global-state';
import i18nextko from "../../components/util/i18nko";
import {autobind, computed} from "knockout-decorators";
import {App} from "../../app";
import {isAllowedDestination} from "../../utils/utils";

class ViewModelContext extends Context {
    destinations: Array<DestinationDto>;
    groupItems: FilterGroupItemsDto;
    templateName?: string;
    imagePath?: string;
    checklist?: ChecklistDto;
    userTemplate?: boolean;
    selectDestinations: Array<DestinationDto>;
    trip?: TripDto;
    destinationNames: string[];
}

class GroupsViewModel {

    /**
     * Trip title.
     */
    public title: KnockoutObservable<string>;

    /**
     * Array with available destinations
     */
    public destinations: KnockoutObservableArray<DestinationDto>;

    /**
     * Selected trip destinations.
     */
    public selectedDestinations: KnockoutObservableArray<DestinationDto>;

    /**
     * Array with available filters for each group.
     */
    public filterGroupItems: KnockoutObservableArray<FilterGroupItemsDto>;

    /**
     * Selected filters map.
     * Key is the group id, value is an array with the selected filter ids.
     */
    public selectedFilters: Map<number, KnockoutObservableArray<number>>;

    /**
     * An validated observable that checks if all validations in this page are valid.
     */
    public validationStatus: KnockoutObservable<any>;

    public tripPicturePath: KnockoutObservable<string>;
    public tripPicturesVisible: KnockoutObservable<boolean>;

    // noinspection TypeScriptFieldCanBeMadeReadonly
    /**
     * Stores checklist from template.
     */
    checklist: ChecklistDto;
    trip: TripDto;

    /**
     * Flag whether the checklist was created based on a template.
     */
    fromTemplate: boolean;
    userTemplate: boolean;

    /**
     * Constructor
     * @param ctx
     */
    constructor(ctx: ViewModelContext) {
        this.trip = ctx.trip;
        this.checklist = ctx.checklist;

        this.fromTemplate = ctx.templateName !== undefined && !ctx.userTemplate;
        this.userTemplate = ctx.userTemplate;

        const titlePlaceholder = GroupsViewModel.titleNumber(ctx.templateName || i18nextko.t("groups.defaultChecklistName")(),
            globalState.trips());
        this.title = ko.observable(titlePlaceholder).extend({required: true});

        this.destinations = ko.observableArray(ctx.destinations);
        this.selectedDestinations = ko.observableArray(ctx.selectDestinations).extend({required: true});

        // convert FilterGroupItemsDto into array of groups with corresponding items
        this.filterGroupItems = ko.observableArray(ctx.groupItems.groups
            .sort((g1, g2) => g1.order - g2.order)
            .map(group =>
                <FilterGroupItemsDto>{
                    groups: [
                        group
                    ],
                    items: getSubfilters(ctx.groupItems, group.id)
                }));

        // initialize selected filter items by group
        this.selectedFilters = new Map<number, KnockoutObservableArray<number>>();
        ctx.groupItems.groups.forEach(group => {
            this.selectedFilters.set(group.id, ko.observableArray<number>())
        });

        // noinspection JSUnusedLocalSymbols
        const flattenedSelectedFilters: KnockoutComputed<number[]> = ko.pureComputed(() => {
            return _flatten(Array.from(this.selectedFilters.values()).map(value => value()));
        });

        this.validationStatus = ko.validatedObservable({
            title: this.title,
            selectedDestinations: this.selectedDestinations
        });

        this.tripPicturePath = ko.observable(ctx.imagePath);
        this.tripPicturesVisible = globalState.tripPicturesVisible;
    }

    /**
     * Show/hide trip images selection.
     */
    @autobind
    public selectTripPicture() {
        if (!this.tripPicturesVisible()) {
            const self = this;
            App.loginRequiredModal(function () {
                window.scrollTo({top: 0, left: 0, behavior: 'auto'});
                self.tripPicturesVisible(true);
            });

        } else {
            window.scrollTo({top: 0, left: 0, behavior: 'auto'});
            this.tripPicturesVisible(false);
        }
    }

    /**
     * Display selected trip picture with fallback.
     */
    @computed
    public get tripPictureStyle() {
        return this.tripPicturePath() ? 'url(' + config.imagePath + '/' + this.tripPicturePath() + ')' :
            `url(${config.defaultImage})`;
    }

    /**
     * Format a destination for the autocomplete dropdown.
     * @param destination
     */
    public formatDestination(destination: DestinationDto): string {
        return destination.name;
    }

    /**
     * Handler for selecting a destination.
     * Returns an empty string to reset the search field.
     * @param destination
     */
    @autobind
    public selectDestination(destination: DestinationDto): string {
        this.selectedDestinations.push(destination);
        return "";
    }

    /**
     * Handler for deselecting a destination.
     * @param destination
     */
    @autobind
    public deselectDestination(destination: DestinationDto): void {
        this.selectedDestinations.remove(destination);
    }

    /**
     * Creates and saves a new checklist from selected filters. It will create a new trip, if it is not created from a
     * template. Finally routes to checklist page.
     */
    @autobind
    public createChecklist() {
        const errors = ko.validation.group(this);
        if (!this.isFromReiseApp() && errors().length > 0) {
            errors.showAllMessages();
            postbox.addError('validation.failed');

        } else {
            globalState.loading(true);

            this.createTripIfAbsent()
                .then(() => this.createChecklistIfAbsent())
                .then(() => this.saveTrip())
                .then(() => this.saveChecklist())
                .then(() => globalState.trips.unshift(this.trip))
                .then(() => globalState.loading(false))
                .then(() =>
                    Router.update(`/reise/${this.trip.id}/checkliste`, {
                        push: false,
                        force: false,
                        with: {showSettingsHint: this.fromTemplate}
                    }))
                .catch(err => {
                    globalState.loading(false);
                    console.error('failed to create checklist', err);
                    postbox.addError('groups.errors.failedToCreateChecklist');
                });
        }
    }

    createTripIfAbsent() {
        if (this.trip) {
            // there is already a trip (template, usertemplate or reise app)
            return myTripsApi.getTripById(this.trip.id.toString())
                .catch(() =>
                    // the user logged in on this page -> we need to map the anonymous trip to user first
                    myTripsApi.getOrCreateAnonymousTripById(this.trip.id.toString()))
                .then(result => this.trip = result.data);

        } else {
            // there is no trip (individual checklist)
            const trip = <TripDto>{
                title: this.title(),
                destinations: this.selectedDestinations().map(destination => ({
                    id: destination.id
                })),
                imageUrl: this.tripPicturePath()
            };

            // create a new trip
            return myTripsApi.createTrip(trip).then(result => this.trip = result.data);
        }
    }

    createChecklistIfAbsent() {
        if (this.checklist) {
            // a checklist has been created already (template or usertemplate)
            return Promise.resolve(this.checklist);

        } else {
            // create a new checklist from selected filters (w/o destinations) (individual)
            return myTripChecklistApi
                .createTripChecklist(this.trip.id.toString(), {
                    selectedFilters: _flatten(
                        Array.from(this.selectedFilters.values())
                            .map(filterItem => filterItem()))
                })
                .then(result => this.checklist = result.data);
        }
    }

    saveTrip() {
        if (!this.isFromReiseApp()) {
            // the trip can be updated in the web only
            this.trip.title = this.title();
            this.trip.imageUrl = this.tripPicturePath();
            this.trip.destinations = this.selectedDestinations().map(destination => ({
                id: destination.id
            }));

            return myTripsApi.updateTrip(this.trip.id.toString(), this.trip)
                .then(result => this.trip = result.data);

        } else {
            // title, image and destinations can not be set in reise app -> nothing to update on the trip
            return Promise.resolve(this.trip);
        }
    }

    saveChecklist() {
        // the checklist needs to be updated with destinations in web only. Destinations can not be changed in reise
        // app.
        if (!this.isFromReiseApp()) {
            const addedDestinations = this.selectedDestinations()
                .map(destination => destination.id);

            return myTripChecklistApi
                .updateTripChecklist(this.trip.id.toString(), {
                    selectedFilters:
                        (this.checklist.createdFromFilters || []).map(filterId => parseInt(filterId)),
                    selectedDestinations: addedDestinations
                })
                .then(result => this.checklist = result.data);

        } else {
            return Promise.resolve(this.checklist);
        }
    }

    isFromReiseApp(): boolean {
        return globalState.isReiseApp() && globalState.reiseAppTrip() !== 'neu';
    }

    static titleNumber(title: string, trips: TripDto[]): string {
        let count = -1;
        const search = `^${title}( ([0-9]+)){0,1}$`;
        trips.map(trip => trip.title)
            .filter(tripTitle => new RegExp(search, 'gi').test(tripTitle))
            .forEach(tripTitle => {
                const matches = new RegExp(search, 'gi').exec(tripTitle);
                if (matches[2]) {
                    const tripCount = parseInt(matches[2]);
                    count = tripCount > count ? tripCount : count;
                } else {
                    count = count != -1 ? count : 0;
                }
            });

        count++;
        return `${title}${count == 0 ? '' : ' ' + count}`;
    }
}

export default <KnockoutLazyPageDefinition>{
    viewModel: GroupsViewModel,
    template: require('./groups.html'),
    componentName: "groups",
    loader: (ctx: ViewModelContext) =>
        // load filters and destinations
        Promise
            .all([
                publicApi.getFilters().then(result => ctx.groupItems = result.data),
                destinationsApi.getDestinations().then(result =>
                    ctx.destinations = result.data.filter(destination =>
                        isAllowedDestination(destination)))
            ])
            .then(() => {
                const destinationNames: string[] = (ctx.destinationNames || []).length > 0 ?
                    ctx.destinationNames :
                    (new URLSearchParams(location.search).get('reiseziele') || '').split(',');
                ctx.selectDestinations = destinationNames
                    .filter(destinationName =>
                        ctx.destinations.some(destination => destination.name == destinationName))
                    .map(destinationName =>
                        ctx.destinations
                            .find(destination => destination.name == destinationName))

                if (ctx.params.tripId) {
                    // load trip, if exists (comes from template, usertemplate or reiseapp)
                    return myTripsApi.getTripById(ctx.params.tripId)
                        .then(result => {
                            ctx.trip = result.data;

                            if (ctx.trip.checklistCreated) {
                                // load the checklist if it has been created already
                                return myTripChecklistApi.getTripChecklist(ctx.params.tripId)
                                    .then(checklistResult => {
                                        ctx.checklist = checklistResult.data;
                                        if ((ctx.trip.destinations || []).length > 0) {
                                            ctx.selectDestinations = ctx.trip.destinations
                                                .map(tripDestination =>
                                                    ctx.destinations
                                                        .find(d => tripDestination.id == d.id) || null)
                                                .filter(tripDestination => tripDestination != null);
                                        }

                                        return Promise.resolve();
                                    });

                            } else {
                                return Promise.resolve();
                            }
                        });

                } else {
                    return Promise.resolve();
                }
            })
};
